<script setup lang="ts">
import {AbsenceType, Employee, PlanningStatusFragment} from "@/graphql/generated/graphql";
import {CalendarMonth} from "@/helper/calendar/calendarMonth";
import {getAutopilotStatusSummary} from "@/helper/autopilotStatus";
import {computed, PropType} from "vue";
import {IonChip, IonIcon, IonLabel} from '@ionic/vue'
import {homeOutline, pauseCircleOutline, scanOutline, stopCircleOutline} from "ionicons/icons";
import InlineSVG from "@/components/InlineSVG.vue";
import {getIconForAbsenceType} from "@/helper/absence";

const props = defineProps({
    calendarMonth: {
        type: Object as PropType<CalendarMonth>,
        required: true,
    },
    employee: {
        type: Object as PropType<Employee>,
        required: true,
    },
    planningStatus: {
        type: Object as PropType<PlanningStatusFragment> | undefined,
        required: false
    },
    compact: {
        type: Boolean,
        default: true
    },
    monthLabelFormat: {
        type: String,
        required: false,
        default: (p: {compact:boolean}) => (p.compact ? 'MMM' : 'MMMM y'),
    }
})

const autopilotStatus = computed(() => props.planningStatus?.autopilotStatus)
const autopilotStatusSummary = computed(() => autopilotStatus.value ? getAutopilotStatusSummary(autopilotStatus.value) : undefined)

const monthLabel = computed(() => props.calendarMonth && props.monthLabelFormat
    ? props.calendarMonth.format(props.monthLabelFormat)
    : ''
)

const plannedAbsences = computed(() => autopilotStatusSummary.value?.plannedAbsenceCount ?? 0)

/**
 * post fetch avaiulability count
 */
const labels = computed(() => ({
  vacation: props.compact ? 'u' : ' Urlaubstage',
  availabilities: props.compact ? 'v' : ' Verfügbarkeiten',
  bookings: props.compact ? 'd' : ' Dienste',
  jobsites: props.compact ? '' : ' Stationen'
}))

</script>

<template>
    <IonChip v-bind="$attrs" v-if="!autopilotStatus || !autopilotStatusSummary" color="dark" :class="{compact}">
        <!-- no autopilot status -->
        <IonLabel v-if="monthLabel">{{monthLabel}}</IonLabel>

        <IonLabel>
            <IonIcon :icon="stopCircleOutline" ></IonIcon>
        </IonLabel>

    </IonChip>

    <IonChip v-bind="$attrs" v-else-if="autopilotStatusSummary && !autopilotStatusSummary.statusIsRunning" color="error" :class="{compact}">
        <!-- autopillot paused -->
        <IonLabel v-if="monthLabel">{{monthLabel}}</IonLabel>
        <IonIcon :icon="pauseCircleOutline"/>
    </IonChip>

    <IonChip v-bind="$attrs" v-else-if="autopilotStatus && autopilotStatus.missingBookings === 0" color="success" :class="{compact}">
        <!-- autopillot finished -->
        <IonLabel v-if="monthLabel">{{monthLabel}}</IonLabel>

        <InlineSVG
            :src="autopilotStatusSummary.worktimeIcon"
        /><IonLabel>{{autopilotStatus.booked}}{{labels.bookings}}</IonLabel>

        <IonLabel v-if="plannedAbsences > 0">
            <IonIcon :icon="getIconForAbsenceType(AbsenceType.Vacation)"/>{{ plannedAbsences }}{{labels.vacation}}
        </IonLabel>
        <!--<IonIcon :icon="checkmarkOutline"></IonIcon>-->
    </IonChip>

    <IonChip v-bind="$attrs"
        v-else-if="autopilotStatus && autopilotStatus.missingBookings > 0"
        :color="autopilotStatus.bookedWorkingHours / autopilotStatus.bookingTargetHours >= 0.8 ? 'warning' : 'danger'"
         :class="{compact}"
    >
        <!-- autopilot running -->

        <IonLabel v-if="monthLabel">{{monthLabel}}</IonLabel>
        <IonLabel>
            <!--{{autopilotStatus.missingBookings
                ? '-' + autopilotStatus.missingBookings + 'd' + (autopilotStatusSummary.worktimeMode === WorktimeMode.SpotOn ? '' : (' (' + autopilotStatus.bookingTarget + 'd)'))
                : (autopilotStatus.booked + 'd')
            }}-->
            <InlineSVG
                :src="autopilotStatusSummary.worktimeIcon"
            />{{ autopilotStatus.booked }} / {{autopilotStatus.bookingTarget}}{{labels.bookings}}
        </IonLabel>

<!--        <IonLabel>-->
<!--            {{autopilotStatus.bookingTarget}}d-->
<!--        </IonLabel>-->

        <IonLabel>
            <IonIcon :icon="scanOutline"/>{{ planningStatus?.availabilities }}{{labels.availabilities}}
        </IonLabel>


        <IonLabel>
            <IonIcon :icon="homeOutline"/>{{ autopilotStatusSummary.jobsiteSummary.automaticallyAddedCount }}+{{autopilotStatusSummary.jobsiteSummary.manuallyAddedCount}}{{labels.jobsites}}
        </IonLabel>

        <IonLabel v-if="plannedAbsences > 0">
            <IonIcon :icon="getIconForAbsenceType(AbsenceType.Vacation)"/>{{ plannedAbsences }}{{labels.vacation}}
        </IonLabel>
    </IonChip>
</template>

<style scoped lang="scss">
    ion-chip:not(.compact) {
        flex-wrap: wrap;
    }

    ion-chip {
        ion-label {
            &:not(:last-child) {
                margin-right: 0.75em
            }

        }

        .inline-svg,
        ion-icon {
            margin-right: 0.25em
        }
    }

    :deep(ion-label) ion-icon {
        display: inline-block;
        vertical-align: bottom;
    }

    :deep(.inline-svg) svg {
        vertical-align: bottom;
    }
    :deep(.inline-svg) {
        width: 1em;
        display: inline-block
    }

</style>
